import React from 'react';
import { FormHelperText, TextField as MuiTextField } from '@mui/material';
import type { AmendatoryLineItemProps } from '../AmendatoryCoverageLineItemFactory';
import CoverageOptionsWrapper from '../shared/CoverageItemsWrapper';
import CoverageButton from '../shared/CoverageButton';
import { Modal } from '../../../../../../../../../../components/modals/v2/helpers/v2.modal.helpers';
import type { WaiverOfSubrogationFormValues } from '../../modals/WaverOfSubrogationModal';
import { WaverOfSubrogationModalConfig } from '../../modals/WaverOfSubrogationModal';
import type { Waiver } from '../../../../../../api/types/rater-x-preset';
import useTypingTimeout from '../../../../hooks/useTypingTimeout';

const WaiverOfSubrogation = (
  props: AmendatoryLineItemProps & { hasAdjustment: boolean }
) => {
  const min = props.rules?.adjustment?.from;
  const max = props.rules?.adjustment?.to;

  const handleAddWaiver = (formValues: WaiverOfSubrogationFormValues) => {
    const _waivers = formatWaiversForState(formValues);

    props.setCoverage({
      id: props.id,
      value: {
        props: {
          items: { values: _waivers as unknown as Waiver[] },
        },
      },
    });
  };

  const validateInput = (value: number) => {
    if (!value) {
      return 'Input required.';
    }

    if (!props.rules?.adjustment?.step) {
      return;
    }

    if (!min || !max) {
      return;
    }

    if (value < +min || +value > +max) {
      return `Out of range. Must be within ${min} and ${max}`;
    }

    if (value % props.rules?.adjustment?.step !== 0) {
      return `Invalid input. Must be a multiple of ${props.rules?.adjustment?.step}`;
    }
  };

  const typingTimeout = useTypingTimeout(
    (newValue: string) => {
      props.setCoverage({
        id: props.id,
        value: { props: { adjustment: { value: Number(newValue) } } },
      });
    },
    {
      validate: validateInput,
      defaultValue: props.value?.props.adjustment?.value,
    }
  );

  return (
    <CoverageOptionsWrapper>
      <CoverageButton
        disabled={!props.isSelected}
        onClick={Modal.deferShow(WaverOfSubrogationModalConfig, {
          data: {
            onSubmit: handleAddWaiver,
            waivers: getDefaultWaiverValues(
              props.value?.props?.items?.values as Waiver[]
            ),
          },
        })}
      >
        Add/Edit ({String(props.value?.props?.items?.values?.length ?? 0)})
      </CoverageButton>

      {props.hasAdjustment && (
        <CoverageOptionsWrapper>
          <MuiTextField
            size="small"
            disabled={!props.isSelected}
            name="waiverOfSubrogationAdjustment"
            type="number"
            value={typingTimeout.input}
            onChange={(e: any) => typingTimeout.onChange(e.target.value)}
            inputProps={{
              step: props.rules?.adjustment?.step,
              min,
              max,
            }}
            sx={{
              '& .MuiInputBase-input': {
                height: '0.75rem',
                width: '8rem',
                textAlign: 'left',
                '&:disabled': {
                  opacity: 0.15,
                },
              },
            }}
          />

          <FormHelperText style={{ marginTop: 0, marginLeft: '0.5rem' }}>
            {typingTimeout.error}
          </FormHelperText>
        </CoverageOptionsWrapper>
      )}
    </CoverageOptionsWrapper>
  );
};

export default WaiverOfSubrogation;

export function getDefaultWaiverValues(values: Waiver[] | undefined) {
  if (!values) {
    return [];
  }

  return values.map((value) => {
    return {
      name: value.value ?? '',
    };
  });
}

export function formatWaiversForState(
  formValues: WaiverOfSubrogationFormValues
) {
  return formValues.waivers.map((waiver) => {
    return { value: waiver.name };
  });
}
